import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Badge, Modal, ModalBody, ModalHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getPublishedReleaseNotes } from "../../store/release-notes/actions";

const FEATURE_TYPES = [
  { type: "new_feature", color: "var(--vz-success)", singular: "releaseNotes.newFeature", plural: "releaseNotes.newFeatures" },
  { type: "improvement", color: "var(--vz-info)", singular: "releaseNotes.improvement", plural: "releaseNotes.improvements" },
  { type: "bug", color: "var(--vz-danger)", singular: "releaseNotes.bugFix", plural: "releaseNotes.bugFixes" },
];

const WhatsNewModal = ({ isOpen, toggle }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(false);

  const lang = i18n.resolvedLanguage === "pt" ? "pt" : "en";

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      dispatch(getPublishedReleaseNotes())
        .then((data) => {
          if (data && Array.isArray(data)) {
            setReleases(data);
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [isOpen]);

  const renderFeature = (feature, idx) => (
    <div key={idx} className="whats-new-feature-item">
      <div className="whats-new-feature-icon">
        <i className={feature.icon || "ri-star-line"}></i>
      </div>
      <div className="whats-new-feature-content">
        <div className="whats-new-feature-title">
          {lang === "pt" ? feature.title_pt : feature.title_en}
        </div>
        {(lang === "pt" ? feature.description_pt : feature.description_en) && (
          <p className="whats-new-feature-description">
            {lang === "pt" ? feature.description_pt : feature.description_en}
          </p>
        )}
      </div>
    </div>
  );

  const renderGroupedFeatures = (features) => {
    if (!features || features.length === 0) return null;

    const grouped = FEATURE_TYPES.map(({ type, color, singular, plural }) => {
      const items = features.filter((f) => f.type === type);
      if (items.length === 0) return null;
      const label = items.length === 1 ? t(singular) : t(plural);
      return (
        <div key={type} className="whats-new-type-box">
          <span className="whats-new-type-label" style={{ backgroundColor: color }}>
            {label}
          </span>
          {items.map((feature, idx) => renderFeature(feature, `${type}-${idx}`))}
        </div>
      );
    });

    // Render features without a recognized type outside any box
    const untyped = features.filter((f) => !f.type || !FEATURE_TYPES.some((ft) => ft.type === f.type));
    return (
      <>
        {grouped}
        {untyped.map((feature, idx) => renderFeature(feature, `untyped-${idx}`))}
      </>
    );
  };

  return (
    <Modal centered isOpen={isOpen} toggle={toggle} fade={false} size="lg" className="whats-new-modal">
      <ModalHeader toggle={toggle}>{t("releaseNotes.whatsNew")}</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : releases.length === 0 ? (
          <div className="text-center py-4 text-muted">{t("releaseNotes.noReleaseNotes")}</div>
        ) : (
          releases.map((release) => (
            <div key={release.id} className="whats-new-release-section">
              <div className="whats-new-release-header">
                <Badge color="primary" pill>
                  v{release.version}
                </Badge>
                <h5 className="whats-new-release-title">
                  {lang === "pt" ? release.title_pt : release.title_en}
                </h5>
                <span className="whats-new-release-date">{release.release_date}</span>
              </div>

              {renderGroupedFeatures(release.features)}
            </div>
          ))
        )}
      </ModalBody>
    </Modal>
  );
};

export default WhatsNewModal;
